(ns journal-app-client.utils.common
  (:require [journal-app-client.utils.interop :as interop]
            [clojure.zip :as z]
            [journal-app-client.utils.formatting :as fmt]
            [reagent.core :as r]
            [journal-app-client.utils.react :refer [use-effect]]
            [tick.core :as t]))

(defonce now (r/atom (t/now)))

(defn update-now-effect [& [ms]]
  (use-effect
    (fn update-now-effect-mount []
      (interop/set-timeout
        {:id       :update-now
         :ms       (or ms 1000)
         :callback #(reset! now (t/now))})
      (fn update-now-effect-dismount []
        (interop/set-timeout
          {:id :update-now
           :action :clear})))
    #js[@now]))

(defn index->day [index]
  (case index
    0 t/MONDAY
    1 t/TUESDAY
    2 t/WEDNESDAY
    3 t/THURSDAY
    4 t/FRIDAY
    5 t/SATURDAY
    6 t/SUNDAY
    nil))

(defn day->index [& [day]]
  (let [day (or day (t/today))
        day (if (t/day-of-week? day) day (t/day-of-week day))]
    (cond (= day t/MONDAY) 0
          (= day t/TUESDAY) 1
          (= day t/WEDNESDAY) 2
          (= day t/THURSDAY) 3
          (= day t/FRIDAY) 4
          (= day t/SATURDAY) 5
          (= day t/SUNDAY) 6
          :else nil)))

(defn deep-merge [a & maps]
  (if (map? a)
    (apply merge-with deep-merge a maps)
    (apply merge-with deep-merge maps)))

(defn dissoc-in
  "Dissociates an entry from a nested associative structure returning a new
  nested structure. keys is a sequence of keys. Any empty maps that result
  will not be present in the new structure."
  [m [k & ks :as keys]]
  (if ks
    (if-let [nextmap (get m k)]
      (let [newmap (dissoc-in nextmap ks)]
        (if (seq newmap)
          (assoc m k newmap)
          (dissoc m k)))
      m)
    (dissoc m k)))

(defn argv->props-children [argv]
  (let [props (when (map? (first argv)) (first argv))
        children (if props (rest argv) argv)]
    [props children]))

(defn rgb-color [colors default-teams color-key alpha]
  (when-let [c (get-in colors [(:color (get default-teams color-key)) :color])]
    (str "rgba(" c ", " (or alpha 1) ")")))

(defn rgb-color-dark [colors default-teams color-key alpha]
  (when-let [c (get-in colors [(:color (get default-teams color-key)) :dark])]
    (str "rgba(" c ", " (or alpha 1) ")")))

(defn move-index [v index-1 index-2 & [index-path]]
  (when v
    (if-not (= (- index-1 index-2) 0)
      (cond (vector? v)
            (let [move-index-up? (> index-1 index-2)
                  v (if move-index-up? (vec (reverse v)) v)
                  first-index (if move-index-up? (- (count v) (inc index-1)) index-1)
                  second-index (if move-index-up? (- (count v) (inc index-2)) index-2)
                  part-1 (subvec v 0 first-index)
                  part-2-temp (subvec v first-index (inc second-index))
                  move-item (first part-2-temp)
                  part-2 (vec (reverse (pop (vec (reverse part-2-temp)))))
                  part-3 (subvec v (inc second-index))
                  merged-vector (into (into (into part-1 part-2) [move-item]) part-3)
                  final-vector (if move-index-up? (vec (reverse merged-vector)) merged-vector)]
              final-vector)
            (map? v)
            (let [move-up? (< index-1 index-2)
                  start-index (if move-up? index-1 index-2)
                  end-index (if move-up? index-2 index-1)]
              (reduce
                (fn [result index]
                  (if index-path
                    (let [map-val (first (filter #(= index (get-in (second %) index-path)) v))
                          new-index (if (= (get-in (second map-val) index-path) index-1)
                                      index-2
                                      (if move-up?
                                        (dec (get-in (second map-val) index-path))
                                        (inc (get-in (second map-val) index-path))))]
                      (assoc-in result (into [(first map-val)] index-path) new-index))
                    (let [map-val (first (filter #(= index (second %)) v))
                          new-index (if (= (second map-val) start-index)
                                      end-index
                                      (if move-up?
                                        (dec (second map-val))
                                        (inc (second map-val))))]
                      (assoc result (first map-val) new-index))))
                v
                (range start-index (inc end-index))))
            :else v)
      v)))

(defn sort-by-numbers-in-string [sorting-string-fn m]
  (sort-by #(let [sorting-string (or (sorting-string-fn %) "")
                  numbers-in-string (reduce (fn [r string] (conj r (js/parseInt string)))
                                      []
                                      (re-seq #"[0-9]" sorting-string))]
              (conj numbers-in-string (fmt/lower-case sorting-string)))
    m))

(defn coll->tree [coll & {:keys [id-key parent-key children-key]
                          :or {id-key :id, parent-key :parent-id, children-key :children}}]
  (let [by-parent (group-by parent-key coll)]
    (z/zipper some? ;; branch?
      #(by-parent (get % id-key)) ;; children
      #(assoc %1 children-key %2) ;; make-node
      (first (by-parent nil))))) ;; root

(defn tree-edit "Calls `edit-fn` on all nodes in a tree that's created from `coll` based on `:id-key`, `:parent-key` and `:children-key`. Returns the tree."
  [coll edit-fn & {:keys [id-key parent-key children-key]
                   :or {id-key :id, parent-key :parent-id, children-key :children}}]
  (let [by-parent (group-by parent-key coll)]
    (loop [z (z/zipper some? ;; branch?
               #(by-parent (get % id-key)) ;; children
               #(assoc %1 children-key %2) ;; make-node
               (first (by-parent nil)))] ;; root
      (if (z/end? z)
        (z/root z)
        (recur (z/next (z/edit z #(edit-fn % z))))))))


(defn tree-edit-branch "Calls `edit-fn` on the nodes and sub-nodes denoted by `branch-id`."
  [coll branch-id edit-fn & {:keys [id-key], :or {id-key :id} :as opts}]
  (apply tree-edit coll
    (fn [node z]
      (if (or (contains? (set (map id-key (z/path z)))
                branch-id)
            (= (id-key node) branch-id))
        (edit-fn node)
        node))
    opts))


(defn tree-flatten [tree children-key]
  (->> (tree-seq children-key children-key tree)
    (map #(dissoc % children-key))))



